import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, errMsg, newIdemKey, type Account, type Card } from "../api";
import { normalizeMoneyInput } from "../lib/money";
import { useMe } from "../session";
import { BRANDS, CardBrand } from "./CardBrand";
import { Modal } from "./Modal";
import { Btn, Field, Input, Notice, Select } from "./ui";

/** Cadastro do cartão de crédito: limite, dia de fechamento, dia de vencimento e a conta bancária (em reais) que paga a fatura. */
export function CardForm({ entityId, card, onClose, onDone }: { entityId: string; card: Card | null; onClose: () => void; onDone: (msg: string, saved: Card) => void }) {
  const { me } = useMe();
  const realm = me.user.realm;
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [name, setName] = useState(card?.name ?? "");
  const [institution, setInstitution] = useState(card?.institution ?? "");
  const [brand, setBrand] = useState(card?.brand ?? "");
  const [limit, setLimit] = useState(card ? card.creditLimit.decimal.replace(".", ",") : "");
  const [closingDay, setClosingDay] = useState(String(card?.closingDay ?? 8));
  const [dueDay, setDueDay] = useState(String(card?.dueDay ?? 15));
  const [payAccountId, setPayAccountId] = useState(card?.payAccountId ?? "");
  const [notes, setNotes] = useState(card?.notes ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const idem = useMemo(() => newIdemKey(), []);

  useEffect(() => {
    void api<Account[]>("GET", `/api/${realm}/accounts?entityId=${entityId}&status=ativa`).then((a) => {
      const brl = a.filter((x) => x.currency === "BRL");
      setAccounts(brl);
      if (!card && brl[0]) setPayAccountId((cur) => cur || brl[0]!.id);
    }).catch((e) => setErr(errMsg(e)));
  }, [realm, entityId, card]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const norm = normalizeMoneyInput(limit);
    if (norm === null || norm.startsWith("-") || /^0+(\.0+)?$/.test(norm)) return setErr("Informe o limite do cartão (maior que zero), por exemplo 5.000,00.");
    const cd = Number(closingDay), dd = Number(dueDay);
    if (!Number.isInteger(cd) || cd < 1 || cd > 28 || !Number.isInteger(dd) || dd < 1 || dd > 28) return setErr("Fechamento e vencimento devem ser dias de 1 a 28.");
    if (!payAccountId) return setErr("Escolha a conta que paga a fatura.");
    setBusy(true);
    try {
      const body = { name, institution: institution.trim() || null, brand: brand || null, creditLimit: norm, closingDay: cd, dueDay: dd, payAccountId, notes: notes.trim() || null };
      if (card) {
        const r = await api<Card>("PATCH", `/api/${realm}/cards/${card.id}`, { version: card.version, ...body });
        onDone(`Cartão "${name.trim()}" atualizado.`, r);
      } else {
        const r = await api<Card>("POST", `/api/${realm}/cards`, { entityId, ...body }, idem);
        onDone(`Cartão "${name.trim()}" criado.`, r);
      }
    } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  }

  return (
    <Modal title={card ? "Editar cartão" : "Novo cartão de crédito"} onClose={onClose}>
      <form onSubmit={submit} noValidate data-card-form>
        {err && <Notice kind="error">{err}</Notice>}
        <Field label="Nome do cartão" required><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required autoComplete="off" name="name" /></Field>
        <div className="grid2">
          <Field label="Instituição"><Input value={institution} onChange={(e) => setInstitution(e.target.value)} maxLength={120} name="institution" /></Field>
          <Field label="Bandeira">
            <div className="brand-pick"><CardBrand brand={brand || null} size={40} /><Select value={brand} onChange={(e) => setBrand(e.target.value)} name="brand"><option value="">Não informada</option>{BRANDS.map((b) => <option key={b.code} value={b.code}>{b.label}</option>)}</Select></div>
          </Field>
        </div>
        <div className="grid2">
          <Field label="Limite (R$)" required><Input value={limit} onChange={(e) => setLimit(e.target.value)} inputMode="decimal" decimals={2} placeholder="0,00" name="creditLimit" /></Field>
          <Field label="Conta que paga a fatura" required>
            <Select value={payAccountId} onChange={(e) => setPayAccountId(e.target.value)} name="payAccount">
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
          </Field>
        </div>
        <div className="grid2">
          <Field label="Dia do fechamento" required><Input type="number" min={1} max={28} value={closingDay} onChange={(e) => setClosingDay(e.target.value)} name="closingDay" /></Field>
          <Field label="Dia do vencimento" required><Input type="number" min={1} max={28} value={dueDay} onChange={(e) => setDueDay(e.target.value)} name="dueDay" /></Field>
        </div>
        <p className="small muted" style={{ marginTop: -4 }}>Compras até o dia do fechamento entram na fatura que fecha no mês. Depois do fechamento do cartão, o fechamento e o vencimento só podem mudar com as faturas em aberto quitadas.</p>
        <Field label="Observações"><Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} name="notes" /></Field>
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><button type="submit" className="btn primary" disabled={busy} data-card-save>{busy ? "Salvando…" : "Salvar"}</button></div>
      </form>
    </Modal>
  );
}
